import React, { useRef, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { galleryImages } from '../utils/mockData';

const Gallery = () => {
  const carouselRef = useRef(null);
  const isScrollingRef = useRef(false);

  // Filter carousel images and duplicate for infinite scroll
  const carouselImages = galleryImages.filter(img => img.fullWidthRow);
  const infiniteImages = [...carouselImages, ...carouselImages, ...carouselImages];

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    // Set initial scroll position to the middle set of images
    carousel.scrollLeft = carousel.scrollWidth / 3;

    const handleScroll = () => {
      if (isScrollingRef.current) return;

      const { scrollLeft, scrollWidth } = carousel;
      const singleSetWidth = scrollWidth / 3;

      // If scrolled to the end of the last set, jump to the middle set
      if (scrollLeft >= singleSetWidth * 2) {
        isScrollingRef.current = true;
        carousel.scrollLeft = scrollLeft - singleSetWidth;
        setTimeout(() => { isScrollingRef.current = false; }, 50);
      }
      // If scrolled to the beginning of the first set, jump to the middle set
      else if (scrollLeft <= 0) {
        isScrollingRef.current = true;
        carousel.scrollLeft = scrollLeft + singleSetWidth;
        setTimeout(() => { isScrollingRef.current = false; }, 50);
      }
    };

    carousel.addEventListener('scroll', handleScroll);
    return () => carousel.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 400;
      const newScrollPosition = carouselRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      carouselRef.current.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header */}
      <section className="bg-gradient-to-r from-cyan-600 to-emerald-600 py-10">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Our Work Gallery
          </h1>
          <p className="text-xl text-cyan-50 max-w-2xl mx-auto">
            See the quality of our work and the results we deliver for our customers.
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* First 9 images in 3x3 grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {galleryImages.filter(img => !img.fullWidthRow).map((image, index) => (
                <Card
                  key={image.id}
                  className="group overflow-hidden border-2 border-gray-100 hover:border-cyan-300 hover:shadow-2xl transition-all duration-300 cursor-pointer"
                >
                  {image.beforeAfterImages ? (
                    image.verticalLayout ? (
                      <div className="relative aspect-[4/3] overflow-hidden grid grid-rows-2 gap-1">
                        <div className="relative overflow-hidden">
                          <img
                            src={image.beforeAfterImages[0]}
                            alt={`${image.title} - Before`}
                            className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                          />
                          <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-full">
                            <span className="text-xs font-bold text-gray-900">Before</span>
                          </div>
                        </div>
                        <div className="relative overflow-hidden">
                          <img
                            src={image.beforeAfterImages[1]}
                            alt={`${image.title} - After`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            style={{ objectPosition: 'center 30%' }}
                          />
                          <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-full">
                            <span className="text-xs font-bold text-gray-900">After</span>
                          </div>
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                            <h3 className="text-xl font-bold mb-1">{image.title}</h3>
                            <p className="text-sm text-gray-200">{image.description}</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="relative aspect-[4/3] overflow-hidden grid grid-cols-2 gap-1">
                        <div className="relative overflow-hidden">
                          <img
                            src={image.beforeAfterImages[0]}
                            alt={`${image.title} - Before`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            style={{
                              objectPosition: image.id === 33 ? 'center 80%' :
                                            image.id === 35 ? 'center top' : 'center center',
                              transform: image.id === 35 ? 'scale(1.2)' : 'none'
                            }}
                          />
                          <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-full">
                            <span className="text-xs font-bold text-gray-900">Before</span>
                          </div>
                        </div>
                        <div className="relative overflow-hidden">
                          <img
                            src={image.beforeAfterImages[1]}
                            alt={`${image.title} - After`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            style={
                              image.id === 33 ? { objectPosition: 'center 80%' } :
                              image.id === 35 ? { objectPosition: 'center 80%' } : {}
                            }
                          />
                          <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-full">
                            <span className="text-xs font-bold text-gray-900">After</span>
                          </div>
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                            <h3 className="text-xl font-bold mb-1">{image.title}</h3>
                            <p className="text-sm text-gray-200">{image.description}</p>
                          </div>
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={image.url}
                        alt={image.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        style={{
                          objectPosition: image.id === 1 || image.id === 2 || image.id === 5 ? 'center top' :
                                         image.id === 3 ? 'center 25%' :
                                         image.id === 6 ? 'center 15%' :
                                         image.id === 31 ? 'center top' :
                                         image.id === 32 ? 'center 10%' : 'center center',
                          transform: image.id === 31 ? 'scale(1.10)' : 'none'
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                          <h3 className="text-xl font-bold mb-1">{image.title}</h3>
                          <p className="text-sm text-gray-200">{image.description}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Carousel Section Header - Full Width */}
      <section className="bg-gradient-to-r from-cyan-600 to-emerald-600 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Want to See More of Our Work?
          </h2>
          <p className="text-xl text-cyan-50 max-w-2xl mx-auto">
            Scroll through our gallery below to see more transformations
          </p>
        </div>
      </section>

      {/* Carousel Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* Additional images in horizontally scrollable row with navigation arrows */}
            <div className="relative group">
              {/* Left Arrow */}
              <button
                onClick={() => scrollCarousel('left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 border-2 border-cyan-500 hover:border-cyan-600"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-6 h-6 text-cyan-600" />
              </button>

              {/* Right Arrow */}
              <button
                onClick={() => scrollCarousel('right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 border-2 border-cyan-500 hover:border-cyan-600"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-6 h-6 text-cyan-600" />
              </button>

              <div ref={carouselRef} className="overflow-x-auto pb-4 scroll-smooth">
                <div className="flex gap-6 min-w-max px-2">
                  {infiniteImages.map((image, index) => (
                    <div
                      key={`${image.id}-${index}`}
                      className="flex-shrink-0 w-64 md:w-72 lg:w-80"
                    >
                      <Card className="overflow-hidden border-2 border-gray-100 hover:border-cyan-300 hover:shadow-2xl transition-all duration-300">
                        <div className="relative aspect-[4/3] overflow-hidden">
                          <img
                            src={image.url}
                            alt={`Gallery image ${image.id}`}
                            className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                            style={
                              image.id === 10 ? { objectPosition: 'center 90%' } :
                              image.id === 11 ? { objectPosition: 'center 15%' } :
                              image.id === 12 ? { objectPosition: 'center 50%' } :
                              image.id === 20 ? { objectPosition: 'center 80%' } :
                              image.id === 21 ? { objectPosition: 'center 20%' } :
                              image.id === 22 ? { objectPosition: 'center 80%' } :
                              image.id === 23 ? { objectPosition: 'center 80%' } :
                              image.id === 24 ? { objectPosition: 'center 15%' } :
                              image.id === 26 ? { objectPosition: 'center 85%' } :
                              image.id === 27 ? { objectPosition: 'center 85%' } :
                              image.id === 28 ? { objectPosition: 'center 80%' } :
                              image.id === 29 ? { objectPosition: 'center 50%' } :
                              { objectPosition: 'center top' }
                            }
                          />
                        </div>
                      </Card>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Before After Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              The Difference We Make
            </h2>
            <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
              From window cleaning to pressure washing, gutter services to soft washing, our comprehensive range of external cleaning services transforms both residential and commercial properties, enhancing curb appeal and maintaining value.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-emerald-600 mb-2">
                  99.9%
                </div>
                <p className="text-gray-700 font-semibold">Satisfaction Rate</p>
                <p className="text-sm text-gray-500 mt-1">From all our customers</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-emerald-600 mb-2">
                  3000+
                </div>
                <p className="text-gray-700 font-semibold">Properties Cleaned</p>
                <p className="text-sm text-gray-500 mt-1">Across Neath and beyond</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-emerald-600 mb-2">
                  ~20mi
                </div>
                <p className="text-gray-700 font-semibold">Service Radius</p>
                <p className="text-sm text-gray-500 mt-1">From Neath town centre</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto bg-gradient-to-r from-cyan-600 to-emerald-600 rounded-2xl p-12 text-white">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Want Results Like These?
            </h2>
            <p className="text-xl text-cyan-50 mb-8">
              Get in touch today and let us make your property sparkle.
            </p>
            <a
              href="/contact"
              className="inline-block bg-white text-cyan-600 font-semibold px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Get Your Free Quote
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
